import {
  AllowedJobTypeList,
  isExtensionPrompt,
  type Metadata,
  type Prompt,
} from "@1js/pl-types";
import { JobTypeWeights, type JobType } from "../data";
import {
  DisplayCategoryKey,
  ExtensionKey,
  JobTypeKey,
  ProductNameKey,
} from "../PromptLibrary/PromptLibrary.data";
import type { Filter, FilterOption } from "./types";

/**
 * Group filter options by group key.
 */
export const groupFilters = (filter: Filter) => {
  return filter.options.reduce(
    (
      acc: {
        [group: string]: {
          value: string;
          group?: string;
        }[];
      },
      curr: {
        value: string;
        group?: string;
      }
    ) => {
      const group = curr.group ?? "NA";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(curr);
      return acc;
    },
    {}
  );
};

const convertMetadataToJobTypeFilters = (metadatas: Metadata[]) => {
  const uniqueJobTypeFilterTracker = new Set<string>();
  let metadataFilters: FilterOption[] = [];
  metadatas.forEach((item: Metadata) => {
    if (AllowedJobTypeList.has(item.Name)) {
      const key = `${item.Name}_${item.Value}`;
      if (!uniqueJobTypeFilterTracker.has(key)) {
        uniqueJobTypeFilterTracker.add(key);
        metadataFilters.push({
          value: item.Value,
          group: item.Name,
        });
      }
    }
  });

  metadataFilters = metadataFilters.sort((a, b) => {
    const aWeight = JobTypeWeights[a.group as JobType] || 0;
    const bWeight = JobTypeWeights[b.group as JobType] || 0;
    return aWeight - bWeight;
  });

  return metadataFilters;
};

/**
 * Populates missing fields for extension prompts. The reason for postprocessing is that 3s sends extension prompts without these fields
 * @param prompts list of prompts
 * @returns prompts with extension prompts having their missing fields populated
 */
export const populateMissingFieldsForPrompts = (
  prompts: Prompt[]
): Prompt[] => {
  return prompts.map((prompt) => {
    if (!prompt.DisplayCategory) {
      prompt = {
        ...prompt,
        DisplayCategory: "Ask",
      };
    }
    if (!prompt.Products) {
      prompt = {
        ...prompt,
        Products: ["BizChat"],
      };
    }
    return prompt;
  });
};

export const buildFilters = (prompts: Prompt[]): Filter[] => {
  if (!prompts || prompts.length === 0) {
    return [];
  }
  prompts = populateMissingFieldsForPrompts(prompts);
  const displayCategories: string[] = [];
  let productNames: string[] = [];
  let metadatas: Metadata[] = [];
  const extensions: string[] = [];

  prompts.forEach((p) => {
    if (p.Origin !== "User") {
      displayCategories.push(p.DisplayCategory.toString());
    }
    if (p.Products) {
      productNames = [...productNames, ...p.Products];
    }

    if (p.Metadata) {
      metadatas = [...metadatas, ...p.Metadata];
    }

    if (p.Origin && p.ProviderName && isExtensionPrompt(p)) {
      extensions.push(p.ProviderName);
    }
  });

  return [
    {
      key: ProductNameKey,
      options: [...new Set(productNames)].map((x) => {
        return { value: x };
      }),
      type: "dropdown",
      isLogValuesInTelemetry: true,
    },
    {
      key: DisplayCategoryKey,
      options: [...new Set(displayCategories)].map((x) => {
        return { value: x };
      }),
      type: "dropdown",
      isLogValuesInTelemetry: true,
    },
    {
      key: JobTypeKey,
      options: convertMetadataToJobTypeFilters(metadatas),
      type: "dropdown",
      isLogValuesInTelemetry: true,
    },
    {
      key: ExtensionKey,
      options: [...new Set(extensions)].map((x) => {
        return { value: x };
      }),
      type: "dropdown",
      // TODO: Need to revisit telemetry logging for extension filters
      isLogValuesInTelemetry: false,
    },
  ];
};
